// Pure .ics / .vcf builders for the client portal, plus a browser download helper.
// No React, no Firestore. Key-date labels reuse CLIENT_PORTAL_DATE_FIELDS.
import { CLIENT_PORTAL_DATE_FIELDS, toClientPortalDate } from './clientPortal.js';

const toIcsDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
};

const escapeIcsText = (value) => String(value || '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');

// Ordered list of exportable events: known key dates first, then custom milestones.
export function buildPortalCalendarEvents(formData = {}) {
    const events = [];
    for (const { key, label } of CLIENT_PORTAL_DATE_FIELDS) {
        const date = toClientPortalDate(formData[key]);
        if (date) events.push({ key, title: label, date: formData[key] });
    }
    for (const m of formData.customMilestones || []) {
        const date = toClientPortalDate(m?.date);
        if (date && m?.title) events.push({ key: m.id || m.title, title: m.title, date: m.date });
    }
    return events;
}

export function buildKeyDatesIcs(events = [], { calendarName = 'Transaction' } = {}) {
    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//KeyFlow//Client Portal//EN',
        'CALSCALE:GREGORIAN',
        `X-WR-CALNAME:${escapeIcsText(calendarName)}`
    ];
    events.forEach((event, index) => {
        const date = toClientPortalDate(event.date);
        if (!date) return;
        const start = toIcsDate(date);
        const end = toIcsDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1));
        lines.push(
            'BEGIN:VEVENT',
            `UID:keyflow-${index}-${start}@portal`,
            `SUMMARY:${escapeIcsText(event.title)}`,
            `DTSTART;VALUE=DATE:${start}`,
            `DTEND;VALUE=DATE:${end}`,
            'END:VEVENT'
        );
    });
    lines.push('END:VCALENDAR');
    return lines.join('\r\n');
}

export function buildAgentVCard({ name = '', phone = '', email = '', workspaceName = '' } = {}) {
    const lines = ['BEGIN:VCARD', 'VERSION:3.0', `FN:${escapeIcsText(name || 'Your agent')}`];
    if (workspaceName) lines.push(`ORG:${escapeIcsText(workspaceName)}`);
    if (phone) lines.push(`TEL;TYPE=CELL:${escapeIcsText(phone)}`);
    if (email) lines.push(`EMAIL;TYPE=INTERNET:${escapeIcsText(email)}`);
    lines.push('END:VCARD');
    return lines.join('\r\n');
}

export function downloadTextFile(filename, mimeType, text) {
    if (typeof document === 'undefined' || typeof URL === 'undefined' || typeof Blob === 'undefined') return;
    const blob = new Blob([text], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}
